import { getTypeById } from './identityTypes';

// The "Profile Type" choices in the Card dashboard (My Profile). Picking one stores its id as the Card's
// profileType and applies a recommended link order (platform ids of the dashboard's built-in links).
export interface CardProfileType {
  id: string;
  label: string;
  emoji: string;
  order: string[];
}

export const CARD_PROFILE_TYPES: CardProfileType[] = [
  {
    id: 'restaurant',
    label: 'Restaurant / Café',
    emoji: '🍽',
    order: ['whatsapp', 'phone', 'location', 'website', 'instagram', 'facebook'],
  },
  {
    id: 'retail',
    label: 'Retail Store',
    emoji: '🏪',
    order: ['whatsapp', 'phone', 'location', 'instagram', 'website', 'facebook'],
  },
  {
    id: 'professional',
    label: 'Professional / Consultant',
    emoji: '💼',
    order: ['linkedin', 'email', 'phone', 'website', 'twitter'],
  },
  {
    id: 'brand',
    label: 'Brand / Agency',
    emoji: '🏢',
    order: ['website', 'instagram', 'facebook', 'youtube', 'twitter', 'tiktok'],
  },
  {
    id: 'celebrity',
    label: 'Celebrity / Creator',
    emoji: '🎤',
    order: ['instagram', 'tiktok', 'youtube', 'twitter', 'whatsapp', 'website'],
  },
  {
    id: 'healthcare',
    label: 'Healthcare / Clinic',
    emoji: '🏥',
    order: ['phone', 'whatsapp', 'email', 'location', 'website'],
  },
  {
    id: 'hotel',
    label: 'Hotel / Hospitality',
    emoji: '🏨',
    order: ['phone', 'whatsapp', 'website', 'location', 'instagram'],
  },
  {
    id: 'ecommerce',
    label: 'E-Commerce / Online Store',
    emoji: '📦',
    order: ['website', 'whatsapp', 'instagram', 'payment', 'email'],
  },
  {
    id: 'event',
    label: 'Event / Entertainment',
    emoji: '🎟',
    order: ['website', 'instagram', 'whatsapp', 'facebook', 'phone'],
  },
  {
    id: 'realestate',
    label: 'Real Estate',
    emoji: '🏡',
    order: ['phone', 'whatsapp', 'email', 'website', 'instagram'],
  },
  {
    id: 'freelancer',
    label: 'Freelancer / Artist',
    emoji: '🎨',
    order: ['website', 'instagram', 'linkedin', 'email', 'whatsapp'],
  },
  {
    id: 'nonprofit',
    label: 'Non-Profit / NGO',
    emoji: '🌱',
    order: ['website', 'email', 'facebook', 'instagram', 'twitter'],
  },
];

export const getCardProfileTypeById = (id: string): CardProfileType | undefined =>
  CARD_PROFILE_TYPES.find(t => t.id === id);

// The profileType a public Card shows: an identity type or a dashboard choice passes through,
// anything else (empty, unknown, "master") is "personal".
export const publicProfileType = (id: string | null | undefined): string =>
  id && (getTypeById(id) || getCardProfileTypeById(id)) ? id : 'personal';

// The identity type behind the Card's CTA, verified badge and contact greeting. Dashboard choices
// that are not identity types use "personal" there.
export const identityTypeIdFor = (id: string): string =>
  getTypeById(id) ? id : 'personal';

// The text of the Card's profile-type pill: the identity type's label first, then the dashboard's.
export const profileTypeLabel = (id: string): string =>
  getTypeById(id)?.label ?? getCardProfileTypeById(id)?.label ?? 'Personal';
